import React, { Component } from 'react';

import classNames from 'classnames';
import { Dropdown } from 'components/dropdown';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import throttle from 'lodash/throttle';
import { inject } from 'mobx-react';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { SOURCE_TYPES } from 'modules/app/services/ga/constants';
import GtagService from 'modules/app/services/ga/GtagService';
import { MembershipStore } from 'modules/membership';
import PropTypes from 'prop-types';

import './Tabs.scss';

const DROPDOWN_WIDTH = 39;
const TAB_MARGIN_RIGHT = 24;

export class Tabs extends Component {
  static propTypes = {
    actived: PropTypes.number,
    beforeTabChange: PropTypes.func,
    children: PropTypes.node,
    className: PropTypes.string,
    decimal: PropTypes.bool,
    hide: PropTypes.number,
    isMobile: PropTypes.bool,
    onTabChange: PropTypes.func,
    tabs: PropTypes.array,
    disable: PropTypes.bool,
    tabHighlight: PropTypes.arrayOf(PropTypes.number),
    track: PropTypes.string,
    dataHubTrack: PropTypes.object,
    onlineEvent: PropTypes.bool
  };

  static defaultProps = {
    actived: 0,
    decimal: false,
    disable: false
  };

  currentTab = -1;

  state = {
    actived: this.props.actived,
    dropdownStatus: false,
    responsiveTabs: [],
    style: {},
    tabs: this.props.tabs
  };

  node = React.createRef();

  tabMarginStyle = { marginRight: TAB_MARGIN_RIGHT };

  componentDidMount() {
    window.addEventListener(
      'resize',
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      throttle(this.updateTabs, 1000)
    );
    this.updateTabs();
  }

  componentWillUnmount() {
    window.removeEventListener(
      'resize',
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      throttle(this.updateTabs, 1000)
    );
  }

  componentDidUpdate(prevProps) {
    const { actived, tabs } = this.props;
    const activedChanged = !isNil(actived) && prevProps.actived !== actived;
    const tabsChanged = !isNil(tabs) && !isEqual(prevProps.tabs, tabs);
    const changed = {};

    activedChanged && Object.assign(changed, { actived });
    tabsChanged && Object.assign(changed, { tabs: cloneDeep(tabs) });

    if (!isEmpty(changed)) {
      this.setState(changed, () => {
        activedChanged && this.indicatorStyle();
        tabsChanged && this.spliceTabs(true);
      });
    }
  }

  activedClass = (index) => {
    const { actived } = this.state;

    if (this.props.hide === index) {
      return 'tab--hide';
    }

    return actived === index ? 'active' : '';
  };

  highlightClass = (index) => {
    const { tabHighlight } = this.props;

    return tabHighlight?.includes(index) ? 'tab--circle-highlight' : '';
  };

  get tabs() {
    const { tabs, responsiveTabs } = this.state;

    return tabs.concat(responsiveTabs);
  }

  get activedTabIsResponsive() {
    const { actived, tabs } = this.state;

    return actived > tabs.length - 1;
  }

  get tabsListWidth() {
    const tabsListEl = this.node?.current;

    return tabsListEl && tabsListEl?.clientWidth;
  }

  getDatahubTrackEventByTabIndex(index) {
    const { dataHubTrack, tabs } = this.props;

    if (dataHubTrack) {
      return {
        group: dataHubTrack.group,
        name: dataHubTrack.name
      };
    }

    if (tabs?.[index]?.dataHubTrack) {
      return {
        group: tabs[index].dataHubTrack.group,
        name: tabs[index].dataHubTrack.name
      };
    }
  }

  changeTab = (index) => {
    const { beforeTabChange, onTabChange, track, tabs, onlineEvent } =
      this.props;

    const sourceType = onlineEvent
      ? SOURCE_TYPES.ONLINE_EVENT
      : SOURCE_TYPES.ONLINE_COURSE;

    track &&
      GtagService.sendPageEvent({
        subsection: track,
        subsection2: tabs[index].track,
        sourceType
      });

    const eventToSendDataHubTrack = this.getDatahubTrackEventByTabIndex(index);

    if (eventToSendDataHubTrack) {
      DatahubService.sendMetrify({
        group: eventToSendDataHubTrack.group,
        name: eventToSendDataHubTrack.name,
        user: { id: MembershipStore.marketplaceId },
        params: { active: true }
      });
    }

    beforeTabChange && beforeTabChange(index);
    onTabChange && onTabChange(index);
    !onTabChange && this.setState({ actived: index });
  };

  indicatorStyle = (forceUpdate = false) => {
    const { actived, tabs } = this.state;
    let left = this.tabsListWidth;
    let width = 0;

    if (this.currentTab !== actived || forceUpdate) {
      if (actived < tabs.length && this.node.current) {
        const tabsNodes = this.node.current.getElementsByClassName('tab__item');
        const actual = tabsNodes[actived];

        if (actual) {
          const tabPosition = actual.getBoundingClientRect().x;
          const tabsPanelPosition = this.node.current.getBoundingClientRect().x;

          left = tabPosition - tabsPanelPosition;
          width = actual.offsetWidth;
        }
      }

      this.setState({ style: { width, left } });
      this.currentTab = actived;
    }
  };

  updateTabs = () => {
    this.setState(
      (prevState) => ({
        tabs: prevState.tabs.concat(prevState.responsiveTabs),
        responsiveTabs: []
      }),
      () => this.spliceTabs(true)
    );
  };

  spliceTabs = (forceUpdate = false) => {
    if (!this.node.current) {
      return;
    }

    const tabsListEl = this.node?.current;
    const tabItemEls = tabsListEl.getElementsByClassName('tab__item');
    const { tabs } = this.state;

    let splitIndex = 0;
    let availableWidth = tabsListEl.clientWidth - DROPDOWN_WIDTH;

    while (
      splitIndex < tabsListEl.childElementCount - 1 &&
      availableWidth > 0
    ) {
      availableWidth -= tabItemEls[splitIndex].clientWidth + TAB_MARGIN_RIGHT;
      splitIndex++;
    }

    if (availableWidth < 0) {
      splitIndex--;
    }

    this.setState(
      {
        responsiveTabs: tabs.slice(splitIndex),
        tabs: tabs.slice(0, splitIndex)
      },
      () => this.indicatorStyle(forceUpdate)
    );
  };

  tabClass = (tabIndex) => {
    const { disable } = this.props;

    return classNames(
      'tab',
      'tab__item',
      this.activedClass(tabIndex),
      this.highlightClass(tabIndex),
      { 'tab__item--disable': disable }
    );
  };

  renderTabNames() {
    const { decimal } = this.props;
    const { style, responsiveTabs, dropdownStatus, tabs } = this.state;

    return (
      <ul
        data-test="c-tabs"
        className="tabs__list"
        key="tabs__list"
        ref={this.node}
      >
        {tabs.map((tab, index) => (
          <li
            data-test="c-tabs-item"
            key={index}
            onClick={() => this.changeTab(index)}
            style={this.tabMarginStyle}
            className={this.tabClass(index)}
          >
            {decimal && `${index + 1}. `}
            {tab.name}
          </li>
        ))}

        <div className="indicator__wrapper">
          <span className="indicator" style={style} />
        </div>

        {responsiveTabs.length > 0 && (
          <Dropdown
            active={dropdownStatus}
            dropdownToggle="···"
            toggleDropdown={() => this.toggleDropdown()}
            classNameToggle={classNames({
              'dropdown__togle--active': this.activedTabIsResponsive
            })}
          >
            {responsiveTabs.map((tab, index) => {
              const tabIndex = tabs.length + index;

              return (
                <li
                  key={index}
                  className={`tab__item ${this.activedClass(
                    tabIndex
                  )} ${this.highlightClass(tabIndex)}`}
                  onClick={() => this.changeTab(tabIndex)}
                >
                  {tab.name}
                </li>
              );
            })}
          </Dropdown>
        )}
      </ul>
    );
  }

  renderTabContents() {
    const { children } = this.props;

    if (!(children && Array.isArray(children))) {
      return <div className="tab__content active">{children}</div>;
    }

    return children.map((child, index) => (
      <div
        className={`tab__content ${this.activedClass(index)}`}
        key={`tab__content__${index}`}
      >
        {child}
      </div>
    ));
  }

  render() {
    const { className } = this.props;
    const classComponent = classNames('tabs', className);

    return (
      <div className={classComponent}>
        {this.renderTabNames()}

        {this.renderTabContents()}
      </div>
    );
  }
}

export default inject((stores) => ({
  onlineEvent: stores.MembershipStore.onlineEvent,
  isMobile: stores.UiStore.isMobile
}))(Tabs);
